export type CaseVisual = "app" | "platform" | "device";

export type ExperienceHighlight = {
  label: string;
  value: string;
};

export type Experience = {
  id: string;
  title: string;
  role: string;
  industry: string;
  situation: string;
  highlights: ExperienceHighlight[];
  approach: string[];
  outcomes: string[];
  tags: string[];
  visual: CaseVisual;
};

export const experiences: Experience[] = [
  {
    id: "device-fleet-firmware",
    visual: "device",
    title: "Private OTA for a device fleet",
    role: "Consulting engineer",
    industry: "Connected hardware",
    situation:
      "Firmware builds were not reproducible. Two images with the same version could differ, and field updates depended on public infrastructure nobody on the product owned. There was no practiced way to pull a bad release back.",
    highlights: [
      { label: "Platform", value: "Embedded Linux" },
      { label: "Stack", value: "OpenWrt, OTA, private CI" },
      { label: "Focus", value: "Firmware and OTA updates" },
    ],
    approach: [
      "Pinned upstream package feeds and recorded the build config so a release could be rebuilt from its own manifest.",
      "Stood up a private update service on an internal mirror so field updates stayed off public infrastructure.",
      "Made promote and rollback scripted steps, then ran the rollback for real before devices depended on it.",
      "Put the pipeline on runners we host on Linux and macOS so CI cost stayed near zero.",
    ],
    outcomes: [
      "A released image can be rebuilt from its manifest.",
      "Field updates run on infrastructure the product controls.",
      "Rollback is practiced, not theoretical.",
    ],
    tags: ["Embedded Linux", "OpenWrt", "OTA", "Reproducible builds"],
  },
  {
    id: "gpu-creative-app",
    visual: "app",
    title: "One rendering engine for app, extension, and headset",
    role: "Consulting engineer",
    industry: "Consumer software",
    situation:
      "Live GPU drawing had to run inside an iMessage extension, where memory is tight. The same editor also had to work in the main app and on a new spatial target without three separate codebases.",
    highlights: [
      { label: "Platform", value: "iOS, macOS, visionOS" },
      { label: "Stack", value: "Swift, Metal, Core Data" },
      { label: "Focus", value: "Metal rendering and App Store releases" },
    ],
    approach: [
      "Split rendering and UI into versioned frameworks so every target shared one engine.",
      "Kept texture work in the engine so the extension stayed under its memory ceiling.",
      "Reduced release to one scripted command: tests, signing, TestFlight.",
      "Added a simulator smoke suite that records video on every CI run.",
    ],
    outcomes: [
      "Shipped to the App Store and kept shipping.",
      "One engine serves the app, the iMessage extension, and the spatial target.",
      "A release is one tagged command, not a checklist.",
    ],
    tags: ["Swift", "Metal", "XCFramework", "Release automation"],
  },
  {
    id: "marketplace-platform",
    visual: "platform",
    title: "Marketplace app with a local-first backend",
    role: "Consulting engineer",
    industry: "Consumer marketplace",
    situation:
      "Booking and a social feed shared one API across mobile and web. Media, auth, email, and push all sat on managed cloud services, so local setup was slow and cloud bugs were hard to catch before deploy.",
    highlights: [
      { label: "Platform", value: "iOS, Android, web" },
      { label: "Stack", value: "React Native, Node, AWS" },
      { label: "Focus", value: "Mobile apps and AWS services" },
    ],
    approach: [
      "React Native clients and a Node API with a document store.",
      "Ran the database and object storage in containers locally so local and cloud differed by config only.",
      "Seeded accounts for every marketplace role so anyone could reproduce a provider or admin path.",
      "Acceptance tests on stories, API and browser checks on the running system, deploy on merge.",
    ],
    outcomes: [
      "Shipped to the App Store.",
      "The stack runs locally with no cloud credentials.",
      "Merge to main deploys. Releases no longer depend on one laptop.",
    ],
    tags: ["React Native", "Node.js", "AWS", "MongoDB", "CI/CD"],
  },
];
